import {useQuery} from '@tanstack/react-query'
import {isAxiosError} from 'axios'
import {QUERY_KEYS} from '@/queryClient'
import {httpGetNotification} from '../http/notifications.http'

/**
 * Whether the failure means "this notification is not addressable by this operator":
 * 404 for a soft-deleted row, 403 for another operator's row.
 */
const isUnresolvableNotification = (error: unknown) =>
    isAxiosError(error) && (error.response?.status === 403 || error.response?.status === 404)

/**
 * Hook to fetch a single notification by id, used to hydrate the work-context bar from
 * the `notification_id` search param without needing the notification list.
 *
 * A stale, foreign or hand-edited id must simply produce no bar and leave the destination
 * rendering normally, so 403/404 are not retried and — unlike most queries here — surface
 * no toast: callers read `data` and render nothing when it is absent.
 */
/**
 * Shared so that optimistic writers address exactly this entry. It sits under the
 * NOTIFICATIONS prefix on purpose: the list mutations already invalidate that prefix, so the
 * bar's notification is refetched alongside the list without any extra bookkeeping.
 */
export const notificationDetailQueryKey = (notificationId?: string) => [
    QUERY_KEYS.NOTIFICATIONS,
    'detail',
    notificationId
]

export const useGetNotification = (notificationId?: string) =>
    useQuery({
        queryKey: notificationDetailQueryKey(notificationId),
        queryFn: () => httpGetNotification(notificationId as string),
        enabled: !!notificationId,
        retry: (failureCount, error) => !isUnresolvableNotification(error) && failureCount < 1
    })
